'use client';

import { useEffect, useState, Suspense, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';

interface ReferralData {
  code: string;
  referrer: string;
  discount?: {
    type: 'percent' | 'fixed';
    value: number;
  };
}

interface StoredReferralCode {
  code: string;
  timestamp: number;
  expiresInDays: number;
}

// Cookie 輔助函數
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift()
    return cookieValue ? decodeURIComponent(cookieValue) : null
  }
  return null
}

const setCookie = (name: string, value: string, days: number = 30) => {
  if (typeof document === 'undefined') return
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires}; SameSite=Lax`
}

// 輔助函數：儲存推薦碼（同時設置 Cookie 和 localStorage 以保持兼容性）
const setReferralCode = (code: string) => {
  // 1. 設置 Cookie（主要方式，與推薦連結一致）
  setCookie('ref_code', code, 30)
  
  // 2. 同時設置 localStorage（向後兼容）
  const data: StoredReferralCode = {
    code,
    timestamp: Date.now(),
    expiresInDays: 20
  };
  localStorage.setItem('referral_code', JSON.stringify(data));
};

// 輔助函數：讀取並檢查推薦碼（優先從 Cookie 讀取）
const getReferralCode = (): string | null => {
  // 1. 先嘗試從 Cookie 讀取（新方式，與推薦連結一致）
  const cookieCode = getCookie('ref_code')
  if (cookieCode) {
    return cookieCode
  }

  // 2. 如果 Cookie 沒有，從 localStorage 讀取（向後兼容）
  const stored = localStorage.getItem('referral_code');
  if (!stored) return null;

  try {
    // 嘗試解析 JSON
    const data = JSON.parse(stored);
    
    // 檢查是否為舊格式（純字串）
    if (typeof data === 'string') {
      // 自動轉換為新格式並同步到 Cookie
      setReferralCode(data);
      return data;
    }

    // 檢查是否為正確格式
    if (!data.code || !data.timestamp || !data.expiresInDays) {
      localStorage.removeItem('referral_code');
      return null;
    }

    // 檢查是否過期（20天 = 1728000000 毫秒）
    const now = Date.now();
    const expiresInMs = data.expiresInDays * 24 * 60 * 60 * 1000;
    if (now - data.timestamp > expiresInMs) {
      localStorage.removeItem('referral_code');
      localStorage.removeItem('referral_data');
      return null;
    }

    // 如果 localStorage 有有效數據但 Cookie 沒有，同步到 Cookie
    if (data.code) {
      setCookie('ref_code', data.code, 30)
    }

    return data.code;
  } catch (e) {
    // JSON 解析失敗，清除無效數據
    localStorage.removeItem('referral_code');
    return null;
  }
};

interface ReferralHandlerProps {
  userId?: string;
  onReferralDetected?: (data: ReferralData) => void;
}

export default function ReferralHandler({ userId, onReferralDetected }: ReferralHandlerProps) {
  const searchParams = useSearchParams();
  const [referralData, setReferralData] = useState<ReferralData | null>(null);

  const handleReferralCode = useCallback(async (code: string) => {
    try {
      // console.log('[ReferralHandler] Processing referral code:', code);
      
      // 1. 先存儲推廣碼到 localStorage
      setReferralCode(code);
      
      // 2. 記錄訪問事件
      const trackResponse = await fetch('/api/referrals/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: code,
          event: 'visit',
          cid: 'homepage'
        })
      });

      if (trackResponse.ok) {
        // console.log('[ReferralHandler] Visit event tracked successfully');
        
        // 設置推廣數據用於顯示
        const referralData: ReferralData = {
          code: code,
          referrer: code  // 暫時使用推廣碼作為推薦者名稱
        };

        setReferralData(referralData);
        onReferralDetected?.(referralData);
      } else {
        const error = await trackResponse.json().catch(() => ({ error: 'Unknown error' }));
        if (process.env.NODE_ENV === 'development') {
          console.error('[ReferralHandler] Failed to track visit:', error);
        }
      }

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[ReferralHandler] Error processing referral code:', error);
      }
    }
  }, [onReferralDetected]);

  useEffect(() => {
    const refCode = searchParams?.get('ref');
    if (refCode) {
      // 不需要等待 userId，訪問時就記錄
      handleReferralCode(refCode);
    }
  }, [searchParams, handleReferralCode]);

  return null; // 此組件不渲染任何 UI
}

